import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import React, { useRef, useState } from 'react'

import { Icon as Iconify } from '@iconify/react'
import StyledBadge from './StyledBadge'
import { appColors } from 'utils'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const isIconifyName = (val) => typeof val === 'string' && val.length > 0 && !val.startsWith('data:image')

const isImgSrc = (val) =>
  typeof val === 'string' && (val.startsWith('data:image') || val.includes('/static/') || val.startsWith('http'))

const renderNavIcon = ({ icon, color, fontSize = 28 }) => {
  if (!icon) return null

  if (React.isValidElement(icon)) return icon

  if (isImgSrc(icon)) {
    return (
      <Box
        component='img'
        src={icon}
        alt=''
        sx={{
          width: fontSize,
          height: fontSize,
          borderRadius: '4px',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    )
  }

  if (isIconifyName(icon)) {
    return <Iconify color={color} icon={icon} fontSize={fontSize} />
  }

  return null
}

const NavBarIcon = ({
  title,
  icon,
  color,
  backgroundColor,
  hoverBackgroundColor,
  onClick,
  badgeContent = null,
  menuItems = null,
  visible = true,
  iconSize = 28,
}) => {
  const { theme, getLogoColor } = useInnovaTheme()
  const ref = useRef(null)
  const [showMenu, setShowMenu] = useState(false)

  const iconColor = color || getLogoColor()

  const defaultTextColor = theme === 'dark' ? appColors.headerTextColor : '#202020'
  if (!visible) return null

  const hasMenu = Array.isArray(menuItems) && menuItems.length > 0

  const onClose = () => setShowMenu(false)

  const handleButtonClick = (event) => {
    event.stopPropagation()

    if (hasMenu) {
      setShowMenu((prev) => !prev)
      return
    }

    if (onClick) onClick(event)
  }

  return (
    <Box ref={ref} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Tooltip
        title={title}
        placement='bottom'
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'rgb(19,62,96)',
              fontSize: '12px',
              fontFamily: 'Roboto',
            },
          },
        }}>
        <IconButton
          size='large'
          onClick={handleButtonClick}
          sx={{
            border: `1px solid ${alpha(iconColor, 0.8)}`,
            borderRadius: '8px',
            backgroundColor: backgroundColor || 'transparent',
            '&:hover': {
              backgroundColor: hoverBackgroundColor || `${alpha(iconColor, 0.2)}`,
              border: `1px solid ${iconColor}`,
            },
            padding: 0.5,
          }}>
          {renderNavIcon({ icon, color: iconColor, fontSize: iconSize })}
          <StyledBadge
            badgecolor={'#0F0'}
            badgeContent={badgeContent}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          />
        </IconButton>
      </Tooltip>

      {hasMenu ? (
        <Menu
          anchorEl={ref.current}
          open={showMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={onClose}
          MenuListProps={{
            onClick: (e) => e.stopPropagation(),
          }}>
          {menuItems.map((item, index) => {
            if (item?.hide) return null
            if (item?.component) {
              return (
                <Box key={index} sx={{ color: item.color ? item.color : defaultTextColor }}>
                  {item.component}
                </Box>
              )
            }

            return (
              <MenuItem
                key={index}
                disabled={item?.disabled}
                onClick={(e) => {
                  e.stopPropagation()
                  if (item.onClick) item.onClick()
                  onClose()
                }}>
                <ListItemIcon>
                  <Iconify icon={item.icon} fontSize={16} color={item.color ? item.color : defaultTextColor} />
                </ListItemIcon>

                <ListItemText
                  sx={{
                    color: item.selected ? appColors.itemTextColor : defaultTextColor,
                  }}>
                  {item.label}
                </ListItemText>
              </MenuItem>
            )
          })}
        </Menu>
      ) : null}
    </Box>
  )
}

export default NavBarIcon
